import { useState } from "react";
import { Check, RotateCcw, ShieldCheck, ShieldOff, UserPlus, X } from "lucide-react";
import { trpc } from "../../hooks/trpc";
import { INSTANCE } from "../../lib/instance";

const SSO_PROVIDER: "microsoft" | "google" = INSTANCE.authProvider;
const SSO_LABEL = SSO_PROVIDER === "google" ? "Google" : "Microsoft";

/** Core-only person management. There is intentionally no role field or role matrix. */
export function CorePeopleView() {
  const utils = trpc.useUtils();
  const peopleQuery = trpc.admin.coreListPeople.useQuery();
  const createMutation = trpc.admin.coreCreatePerson.useMutation({ onSuccess: () => void peopleQuery.refetch() });
  const administratorMutation = trpc.admin.coreSetAdministrator.useMutation({ onSuccess: () => void peopleQuery.refetch() });
  const grantMutation = trpc.admin.coreSetAppGrant.useMutation({ onSuccess: () => void peopleQuery.refetch() });
  const activeMutation = trpc.admin.coreSetPersonActive.useMutation({ onSuccess: () => void peopleQuery.refetch() });
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ username: "", name: "", email: "" });

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (form.username.trim().length < 3) {
      setError("Benutzername muss mindestens 3 Zeichen lang sein.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Für eine SSO-Person ist eine gültige E-Mail-Adresse erforderlich.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        username: form.username.trim(),
        name: form.name.trim() || undefined,
        email: form.email.trim(),
        authProvider: SSO_PROVIDER,
      });
      setShowCreate(false);
      setForm({ username: "", name: "", email: "" });
      await utils.admin.coreListPeople.invalidate();
    } catch (cause) {
      setError((cause as Error).message || "Person konnte nicht angelegt werden.");
    }
  };

  if (peopleQuery.isLoading) return <div className="cc-card"><div className="cc-card-body">Personen werden geladen…</div></div>;
  if (peopleQuery.error) return <div className="cc-card"><div className="cc-card-body"><span className="cc-badge-st is-bad">Fehler</span> {peopleQuery.error.message}</div></div>;

  const people = peopleQuery.data?.people ?? [];
  const apps = peopleQuery.data?.apps ?? [];
  const isPending = administratorMutation.isPending || grantMutation.isPending || activeMutation.isPending;

  return (
    <div className="animate-in" data-testid="core-people">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <div>
          <p className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {people.length} Personen · individuelle Apphäkchen
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--color-muted-foreground)" }}>
            Jede Person sieht nur ihre eigenen Inhalte. Administratorrechte und App-Zugriff legst du getrennt fest.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => { setShowCreate((shown) => !shown); setError(null); }}>
          {showCreate ? <><X size={14} strokeWidth={1.5} /> Abbrechen</> : <><UserPlus size={14} strokeWidth={1.5} /> Neue Person</>}
        </button>
      </div>

      {showCreate ? (
        <form onSubmit={create} className="card p-6 mb-5" data-testid="core-person-create">
          <h3 className="text-sm font-semibold mb-2">Neue Person</h3>
          <p className="text-xs mb-5" style={{ color: "var(--color-muted-foreground)" }}>
            Neue Personen starten ohne App-Zugriff. Die Häkchen setzt du anschließend.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-xs">Benutzername<input className="input mt-2" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} autoComplete="username" /></label>
            <label className="text-xs">Name<input className="input mt-2" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} autoComplete="name" /></label>
            <label className="text-xs">{SSO_LABEL}-E-Mail *<input className="input mt-2" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} autoComplete="email" /></label>
          </div>
          <div className="flex justify-end mt-5"><button type="submit" className="btn btn-success" disabled={createMutation.isPending}>{createMutation.isPending ? "Erstellt…" : "Person anlegen"}</button></div>
          {error ? <p role="alert" className="mt-4 text-xs" style={{ color: "var(--color-danger)" }}>{error}</p> : null}
        </form>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="table" aria-label="Core Personen und Apphäkchen">
          <thead><tr>
            <th>Person</th><th>Administrator</th>
            {apps.map((app) => <th key={app.key} className="text-center">{app.label}</th>)}
            <th>Status</th>
          </tr></thead>
          <tbody>
            {people.map((person) => (
              <tr key={person.id} data-testid={`core-person-${person.id}`} style={person.isActive ? undefined : { opacity: 0.55 }}>
                <td>
                  <div className="font-medium">{person.name ?? person.username}</div>
                  <div className="text-xs" style={{ color: "var(--color-muted)" }}>@{person.username}{person.email ? ` · ${person.email}` : ""}</div>
                </td>
                <td>
                  <label className="flex items-center gap-2 text-xs" title={person.isLastAdministrator ? "Mindestens eine Person muss Administrator bleiben." : undefined}>
                    <input
                      aria-label={`${person.username}: Administrator`}
                      type="checkbox"
                      checked={person.isAdministrator}
                      disabled={!person.isActive || person.isLastAdministrator || isPending}
                      title={person.isLastAdministrator ? "Mindestens eine Person muss Administrator bleiben." : undefined}
                      onChange={() => administratorMutation.mutate({ userId: person.id, isAdministrator: !person.isAdministrator })}
                    />
                    {person.isAdministrator ? <ShieldCheck size={14} strokeWidth={1.5} /> : null}
                  </label>
                </td>
                {apps.map((app) => {
                  const granted = person.grants.includes(app.key);
                  const pending = grantMutation.isPending && grantMutation.variables?.userId === person.id && grantMutation.variables.appKey === app.key;
                  return <td key={app.key} className="text-center"><input
                    aria-label={`${person.username}: ${app.label}`}
                    type="checkbox"
                    checked={granted}
                    disabled={!person.isActive || isPending || pending}
                    onChange={() => grantMutation.mutate({ userId: person.id, appKey: app.key, granted: !granted })}
                  /></td>;
                })}
                <td>
                  {person.isActive ? (
                    <button type="button" className="btn btn-secondary py-1 px-2 text-xs" disabled={person.isLastAdministrator || isPending} onClick={() => {
                      if (confirm("Person deaktivieren? Laufende Sitzungen werden beendet.")) activeMutation.mutate({ userId: person.id, active: false });
                    }}><ShieldOff size={12} strokeWidth={1.5} /> Deaktivieren</button>
                  ) : (
                    <button type="button" className="btn btn-secondary py-1 px-2 text-xs" disabled={isPending} onClick={() => activeMutation.mutate({ userId: person.id, active: true })}><RotateCcw size={12} strokeWidth={1.5} /> Reaktivieren</button>
                  )}
                </td>
              </tr>
            ))}
            {people.length === 0 ? <tr><td colSpan={apps.length + 3} className="text-xs" style={{ color: "var(--color-muted)" }}>Keine Personen vorhanden.</td></tr> : null}
          </tbody>
        </table>
      </div>
      {(administratorMutation.error || grantMutation.error || activeMutation.error) ? <p role="alert" className="mt-3 text-xs" style={{ color: "var(--color-danger)" }}>{administratorMutation.error?.message ?? grantMutation.error?.message ?? activeMutation.error?.message}</p> : null}
    </div>
  );
}
